import React, { useEffect, useMemo, useState } from 'react';
import { extendTheme, ThemeProvider as ChakraThemeProvider } from '@chakra-ui/react';
import { createTheme, ThemeProvider as MaterialThemeProvider } from '@material-ui/core/styles';
import { chakraTheme, muiTheme } from '../App/theme';
import './Admin.css';
import { ChevronDownIcon, CloseIcon, ArrowDownIcon, SquareIcon, SquareCheckIcon, SquareMinusIcon, CircleIcon, CircleDotIcon } from './Components/AdminIcon';

// Staff controls share one density across Chakra, Material UI and legacy CSS.
// Providers also carry these roles into portalled dialogs without touching kiosks.
const control = { borderRadius: '8px', fontSize: { base: '16px', md: '14px' } };
const outlinedField = { field: { borderColor: 'gray.500', _placeholder: { color: 'gray.600', opacity: 1 } } };
const fieldSizes = {
  md: { field: { h: 10, minH: 10, px: 3, fontSize: { base: '16px', md: '14px' } } },
  sm: { field: { h: 8, minH: 8, px: 3, fontSize: { base: '16px', md: '14px' } } },
};
const adminChakraTheme = extendTheme(chakraTheme, {
  components: {
    Button: {
      baseStyle: { borderRadius: '8px', fontWeight: 600, lineHeight: 1.25, flexShrink: 0, _focusVisible: { outline: '3px solid', outlineColor: 'blue.600', outlineOffset: '-3px' } },
      defaultProps: { size: 'md', variant: 'solid' },
      variants: { solid: ({ colorScheme = 'gray' }) => ({
        bg: colorScheme === 'gray' ? 'gray.100' : `${colorScheme}.600`,
        color: colorScheme === 'gray' ? 'gray.800' : 'white',
        backgroundImage: 'none', boxShadow: 'none',
        _hover: { bg: colorScheme === 'gray' ? 'gray.200' : `${colorScheme}.700` },
        _active: { bg: colorScheme === 'gray' ? 'gray.300' : `${colorScheme}.800` },
      }), brand: {
        bg: 'blue.600', backgroundImage: 'none', color: 'white', boxShadow: 'none',
        transition: 'background-color 150ms ease',
        _hover: { bg: 'blue.700', backgroundImage: 'none', boxShadow: 'none' },
        _active: { bg: 'blue.800', backgroundImage: 'none', transform: 'none' },
        _disabled: { bg: 'blue.600', backgroundImage: 'none', opacity: 0.5 },
      } },
      sizes: {
        md: { h: 10, minH: 10, px: 4, fontSize: 'sm' },
        sm: { h: 8, minH: 8, px: 3, fontSize: 'sm' },
        xs: { h: 6, minH: 6, px: 2, fontSize: 'xs' },
      },
    },
    Input: { baseStyle: { field: control }, variants: { outline: outlinedField }, sizes: fieldSizes, defaultProps: { size: 'md', variant: 'outline' } },
    Select: { baseStyle: { field: control }, variants: { outline: outlinedField }, sizes: fieldSizes, defaultProps: { size: 'md', variant: 'outline' } },
    Textarea: { baseStyle: control, variants: { outline: { borderColor: 'gray.500', _placeholder: { color: 'gray.600', opacity: 1 } } }, sizes: { md: { fontSize: { base: '16px', md: '14px' }, lineHeight: 1.5 }, sm: { fontSize: { base: '16px', md: '14px' }, lineHeight: 1.5 } }, defaultProps: { size: 'md', variant: 'outline' } },
    FormError: { baseStyle: { text: { color: 'red.600' }, icon: { color: 'red.600' } } },
    FormLabel: { baseStyle: { fontSize: 'sm', fontWeight: 600, mb: 2 } },
    Form: { baseStyle: { helperText: { fontSize: 'sm', mt: 2 } } },
    Menu: { baseStyle: { item: { fontSize: 'sm', lineHeight: 1.5 } } },
    Modal: { baseStyle: {
      dialog: {
        borderRadius: '12px', color: 'gray.800', fontSize: 'md',
        width: 'calc(100% - 32px)', minW: 0, mx: 4, my: 4,
        // iOS Safari zooms focused text fields below 16px, clipping the modal.
        '& input, & select, & textarea': { fontSize: { base: '16px', md: '14px' }, minWidth: 0 },
      },
      header: { fontSize: 'xl', lineHeight: 1.3, fontWeight: 600, px: { base: 4, md: 6 }, pr: 12, pt: { base: 4, md: 6 }, pb: { base: 3, md: 4 }, flexShrink: 0, overflowWrap: 'anywhere' },
      body: { px: { base: 4, md: 6 }, py: 2, minW: 0, minH: 0, overflowY: 'auto', overflowWrap: 'anywhere' },
      footer: { px: { base: 4, md: 6 }, pt: { base: 3, md: 4 }, pb: { base: 4, md: 6 }, gap: 2, flexWrap: 'wrap', flexShrink: 0 },
    } },
    Alert: { baseStyle: { container: { borderRadius: '8px', fontSize: 'sm' } } },
    Badge: { baseStyle: { borderRadius: '4px', fontWeight: 600, textTransform: 'none', flexShrink: 0, lineHeight: 1.5, px: 2, py: 0.5, whiteSpace: 'nowrap' } },
    Tag: { baseStyle: { container: { borderRadius: '4px', maxW: 'full', fontWeight: 500 }, label: { whiteSpace: 'normal', overflowWrap: 'anywhere' } } },
    Tabs: { sizes: {
      md: { tab: { fontSize: 'sm', lineHeight: 1.5 } },
      sm: { tab: { fontSize: 'sm', lineHeight: 1.5 } },
    }, baseStyle: {
      tablist: { gap: 2, overflowX: 'auto', flexShrink: 0 },
      tab: { flexShrink: 0, whiteSpace: 'nowrap', fontSize: 'sm', fontWeight: 600, minH: 10, px: 4 },
      tabpanel: { px: 0, py: 4, minW: 0 },
    } },
    Table: { sizes: {
      sm: { th: { px: 4, py: 3, fontSize: 'sm', lineHeight: 1.5 }, td: { px: 4, py: 3, fontSize: 'sm', lineHeight: 1.5 } },
      md: { th: { fontSize: 'sm', lineHeight: 1.5 }, td: { fontSize: 'sm', lineHeight: 1.5 } },
    }, baseStyle: {
      th: { fontSize: 'sm', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal' },
      td: { fontSize: 'sm', lineHeight: 1.5 },
    } },
  },
});

// Rebuild typography from the staff base size instead of merging the app's
// already-computed 20px variants (caption, menus and pagination included).
const adminMuiTheme = createTheme({
  ...muiTheme,
  palette: { primary: { main: chakraTheme.colors.blue[600], dark: chakraTheme.colors.blue[700], contrastText: '#fff' }, error: { main: chakraTheme.colors.red[600] } },
  typography: {
    fontFamily: chakraTheme.fonts.body,
    fontSize: 14,
    body1: { fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    h6: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 8 },
  props: {
    MuiSelect: { IconComponent: ChevronDownIcon },
    MuiNativeSelect: { IconComponent: ChevronDownIcon },
    MuiTableSortLabel: { IconComponent: ArrowDownIcon },
    MuiCheckbox: { icon: <SquareIcon />, checkedIcon: <SquareCheckIcon />, indeterminateIcon: <SquareMinusIcon /> },
    MuiRadio: { icon: <CircleIcon />, checkedIcon: <CircleDotIcon /> },
    MuiAutocomplete: { popupIcon: <ChevronDownIcon />, closeIcon: <CloseIcon /> },
    MuiChip: { deleteIcon: <CloseIcon /> },
    MuiTextField: { variant: 'outlined', size: 'small' },
    MuiFormControl: { variant: 'outlined', size: 'small' },
    MuiOutlinedInput: { notched: false },
    MuiButton: { disableElevation: true },
  },
  overrides: {
    MuiButton: {
      root: { minHeight: 40, padding: '8px 16px', lineHeight: 1.25, boxShadow: 'none' },
      sizeSmall: { minHeight: 32, padding: '4px 12px' },
    },
    MuiIconButton: { root: { padding: 8, width: 40, height: 40 }, sizeSmall: { padding: 4, width: 32, height: 32 } },
    // SwitchBase inherits IconButton; fixed button dimensions offset its thumb
    // from the track. Let the switch's own thumb and padding determine its size.
    MuiSwitch: { switchBase: { width: 'auto', height: 'auto' } },
    MuiTableCell: {
      root: { padding: '12px 16px', borderBottomColor: chakraTheme.colors.gray[200] },
      head: { fontWeight: 600, backgroundColor: chakraTheme.colors.gray[50] },
    },
    MuiInputBase: { root: { fontSize: '0.875rem', lineHeight: 1.5, '@media (max-width: 767px)': { fontSize: '16px' } }, input: { boxSizing: 'border-box' } },
    MuiInput: { formControl: { 'label + &': { marginTop: 0 } } },
    MuiOutlinedInput: {
      root: { minHeight: 40, backgroundColor: 'white' },
      input: { padding: '10px 12px', height: 40 },
      inputMarginDense: { padding: '10px 12px' },
      notchedOutline: { borderColor: chakraTheme.colors.gray[500] },
    },
    MuiSelect: { select: { minHeight: 20, paddingRight: 32 } },
    MuiAutocomplete: {
      inputRoot: {
        '&[class*="MuiOutlinedInput-root"]': { padding: 0, paddingRight: 32,
          '& $input': { padding: '10px 12px', height: 40, minWidth: 0 } },
      },
      endAdornment: { right: 0, top: 'calc(50% - 16px)' },
      popupIndicator: { width: 32, height: 32, padding: 6 },
      clearIndicator: { width: 32, height: 32, padding: 6 },
    },
    MuiFormControl: { root: { minWidth: 0, margin: 0 } },
    MuiMenuItem: { root: { fontSize: '0.875rem', lineHeight: 1.5 } },
    MuiTooltip: { tooltip: { fontSize: '0.875rem', lineHeight: 1.5 } },
    MuiFormControlLabel: { label: { fontSize: '0.875rem', lineHeight: 1.5 } },
    MuiFormLabel: { root: { fontSize: '0.875rem' } },
    MuiInputLabel: {
      root: { position: 'static', transform: 'none', lineHeight: 1.5, fontWeight: 600, color: chakraTheme.colors.gray[700], marginBottom: 8 },
      formControl: { position: 'static', transform: 'none' },
      marginDense: { transform: 'none' },
      outlined: { transform: 'none', '&$shrink': { transform: 'none' }, '&$marginDense': { transform: 'none' } },
      shrink: { transform: 'none' },
    },
    MuiFormHelperText: { root: { fontSize: '0.875rem', lineHeight: 1.5 } },
    MuiDialogTitle: { root: { padding: '24px 24px 16px' } },
    MuiDialogContent: { root: { padding: '8px 24px' } },
    MuiDialogActions: { root: { padding: '16px 24px 24px' } },
  },
});

function readVisualViewport() {
  if (typeof window === 'undefined') return null;
  const viewport = window.visualViewport;
  return { width: viewport?.width || window.innerWidth, height: viewport?.height || window.innerHeight,
    top: viewport?.offsetTop || 0, left: viewport?.offsetLeft || 0 };
}

export default function AdminTheme({ children }) {
  const [viewport, setViewport] = useState(readVisualViewport);
  useEffect(() => {
    const update = () => setViewport(previous => {
      const next = readVisualViewport();
      return previous && Object.keys(next).every(key => next[key] === previous[key]) ? previous : next;
    });
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const focused = document.activeElement;
      if (focused?.closest('.chakra-modal__body')) focused.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [viewport]);
  // Portalled dialogs follow the visible area when a software keyboard or
  // browser zoom changes it, without modifying viewport settings or kiosk UI.
  const theme = useMemo(() => viewport ? extendTheme(adminChakraTheme, {
    components: { Modal: { baseStyle: {
      dialogContainer: { width: `${viewport.width}px`, height: `${viewport.height}px`,
        top: `${viewport.top}px`, left: `${viewport.left}px`,
        '@supports(height: -webkit-fill-available)': { height: `${viewport.height}px` } },
      dialog: { maxH: `calc(${viewport.height}px - 32px)` },
    } } },
  }) : adminChakraTheme, [viewport]);
  return (
    <ChakraThemeProvider theme={theme}>
      <MaterialThemeProvider theme={adminMuiTheme}>{children}</MaterialThemeProvider>
    </ChakraThemeProvider>
  );
}
